import { HttpClientInterface } from '@/services/integration/http/httpClientInterface';
import {
  BankAccountTokenisationRequest,
  BankAccountTokenisationResponse,
  GenerateClientTokenRequest,
  GenerateClientTokenResponse,
  MonoovaTokenProviderInterface
} from '@/services/integration/clients/monoova/monoovaTokenProviderInterface';
import { injectable } from 'inversify';
import { HTTPError } from 'ky';

/**
 * MonoovaController returns a JSON body of the form `{ error: string }` when it
 * catches an upstream HttpExceptionInterface, but ky throws before that body is
 * read. Recover the specific message where possible, falling back to whatever
 * error we were given (e.g. a network failure has no response to parse).
 */
async function toMonoovaError (error: unknown): Promise<Error> {
  if (error instanceof HTTPError) {
    const body = await error.response.json().catch(() => null);
    if (body && typeof body.error === 'string') {
      return new Error(body.error);
    }
  }

  return error instanceof Error ? error : new Error(String(error));
}

@injectable()
export default class MonoovaTokenProvider implements MonoovaTokenProviderInterface {
  private client: HttpClientInterface;

  constructor (client: HttpClientInterface) {
    this.client = client;
  }

  async generateClientToken (brand: string, request: GenerateClientTokenRequest): Promise<GenerateClientTokenResponse> {
    try {
      return await this.client.post<GenerateClientTokenResponse>(`api/v1/monoova/${brand}/token/generate`, {
        json: request
      });
    } catch (error) {
      throw await toMonoovaError(error);
    }
  }

  async tokeniseBankAccount (brand: string, request: BankAccountTokenisationRequest): Promise<BankAccountTokenisationResponse> {
    try {
      return await this.client.post<BankAccountTokenisationResponse>(`api/v1/monoova/${brand}/bank-account/token`, {
        json: request
      });
    } catch (error) {
      throw await toMonoovaError(error);
    }
  }
}
